// Visit time slots. The clinic sets its slots once; the reminder call offers the mother only slots that still have
// space and the server records the one she picks (facilities/{fid}/bookings/{itemId}). Reception can also book,
// move or cancel by hand. Rules mirror functions/src/calling/slots.ts (cleanPlan, freeSlots).
// Demo mode: in-memory lists in mockData. Real mode: Firestore (Mumbai), under the user's permissions.
import { collection, doc, getDoc, getDocs, query, setDoc, updateDoc, where } from 'firebase/firestore';
import type { Booking, Role, SlotAgent, SlotDef, SlotPlan, SlotSettings, SlotVisits } from '../types';
import { addDays, parseDate, startOfToday, toIsoDate } from '../lib/dates';
import { db } from '../lib/firebase';
import { bookings as mockBookings, slotSettings as mockSettings } from './mockData';
import { addAudit, type Actor } from './auditService';
import { isReal, loadClinic } from './store';
import { scope } from './scope';
import { respond } from './respond';

export const SLOT_AGENTS: SlotAgent[] = ['baby', 'pregnancy'];
export const MAX_SLOTS = 12;

/** Starting point shown when nothing is saved: Mon–Sat, three one-hour morning slots, 6 mothers each. */
export const DEFAULT_SLOT_PLAN: SlotPlan = {
  days: [1, 2, 3, 4, 5, 6],
  slots: [
    { start: '10:00', end: '11:00', capacity: 6 },
    { start: '11:00', end: '12:00', capacity: 6 },
    { start: '12:00', end: '13:00', capacity: 6 },
  ],
};

const HHMM = /^([01]\d|2[0-3]):[0-5]\d$/;
const ISO = /^\d{4}-\d{2}-\d{2}$/;
const copyPlan = (p: SlotPlan): SlotPlan => ({ days: [...p.days], slots: p.slots.map((s) => ({ ...s })) });

/** Who may change slots and book by hand: the clinic's admin, reception and doctors. */
// ---------- Clinic opening hours (clinic admin only; mirror of functions/src/calling/slots.ts) ----------
export interface OpeningHours { start: string; end: string; set?: boolean; updatedByName?: string; updatedAt?: string }
export const DEFAULT_OPENING_HOURS = { start: '09:00', end: '20:00' };
/** Valid opening hours, or the default 09:00–20:00 (set = false). */
export function openingHoursOf(v: unknown): OpeningHours {
  const o = (v ?? {}) as Partial<OpeningHours>;
  const ok = typeof o.start === 'string' && typeof o.end === 'string' && HHMM.test(o.start) && HHMM.test(o.end) && o.end > o.start;
  return ok ? { start: o.start!, end: o.end!, set: true, updatedByName: o.updatedByName, updatedAt: o.updatedAt } : { ...DEFAULT_OPENING_HOURS, set: false };
}
export const withinHours = (s: { start: string; end: string }, h: { start: string; end: string }) => s.start >= h.start && s.end <= h.end;
const mockHours: Record<string, unknown> = {};
export async function getOpeningHours(fid: string): Promise<OpeningHours> {
  if (isReal()) return openingHoursOf((await getDoc(doc(db!, 'facilities', fid, 'settings', 'openingHours'))).data());
  return respond(openingHoursOf(mockHours[fid]));
}
export async function saveOpeningHours(fid: string, h: { start: string; end: string }, actor: Actor | undefined): Promise<void> {
  if (actor?.role !== 'facility_admin') throw new Error('Not allowed');
  if (!HHMM.test(h.start) || !HHMM.test(h.end) || h.end <= h.start) throw new Error('HOURS_ORDER');
  const out = { start: h.start, end: h.end, updatedBy: signedInUid(actor), updatedByName: actor.name, updatedAt: new Date().toISOString() };
  if (isReal()) await setDoc(doc(db!, 'facilities', fid, 'settings', 'openingHours'), out);
  else mockHours[fid] = out;
  addAudit(actor, 'settings.opening_hours', fid, fid, `${h.start}–${h.end} IST`);
  if (!isReal()) await respond(undefined);
}

export const canEditSlots = (role: Role) => role === 'facility_admin' || role === 'staff' || role === 'doctor';

/** Same as cleanPlan on the server: valid times, end after start, capacity 1–50, at most 12 slots, sorted. */
export function cleanPlan(p: unknown): SlotPlan {
  const raw = (p ?? {}) as Partial<SlotPlan>;
  const days = Array.from(new Set((Array.isArray(raw.days) ? raw.days : []).map(Number).filter((d) => Number.isInteger(d) && d >= 0 && d <= 6))).sort();
  const slots = (Array.isArray(raw.slots) ? raw.slots : [])
    .map((s) => ({ start: String(s?.start ?? ''), end: String(s?.end ?? ''), capacity: Math.min(Math.max(Math.round(Number(s?.capacity) || 0), 1), 50) }))
    .filter((s) => HHMM.test(s.start) && HHMM.test(s.end) && s.end > s.start)
    .sort((a, b) => a.start.localeCompare(b.start))
    .slice(0, MAX_SLOTS);
  return { days, slots };
}

/** Problems a screen should show before saving (the save itself also cleans the plan). */
export function slotProblems(p: SlotPlan, hours?: { start: string; end: string }): ('time' | 'order' | 'capacity' | 'tooMany' | 'overlap' | 'hours')[] {
  const out = new Set<'time' | 'order' | 'capacity' | 'tooMany' | 'overlap' | 'hours'>();
  if (p.slots.length > MAX_SLOTS) out.add('tooMany');
  p.slots.forEach((s) => {
    if (!HHMM.test(s.start) || !HHMM.test(s.end)) out.add('time');
    else if (s.end <= s.start) out.add('order');
    else if (hours && !withinHours(s, hours)) out.add('hours');
    if (!Number.isInteger(s.capacity) || s.capacity < 1 || s.capacity > 50) out.add('capacity');
  });
  const sorted = [...p.slots].sort((a, b) => a.start.localeCompare(b.start));
  sorted.forEach((s, i) => { if (i > 0 && s.start === sorted[i - 1].start) out.add('overlap'); });
  return Array.from(out);
}

const withDefaults = (s: Partial<SlotSettings> | undefined): SlotSettings => ({
  ...s,
  enabled: !!s?.enabled,
  baby: s?.baby ? cleanPlan(s.baby) : copyPlan(DEFAULT_SLOT_PLAN),
  pregnancy: s?.pregnancy ? cleanPlan(s.pregnancy) : copyPlan(DEFAULT_SLOT_PLAN),
  closedDates: (s?.closedDates ?? []).filter((d) => ISO.test(d)).sort(),
});

const weekday = (iso: string) => parseDate(iso).getDay();

/** Is this a slot day for that call type (slots on, not closed, the clinic sees mothers that day)? */
export function isSlotDay(settings: SlotSettings | undefined, agent: SlotAgent, date: string): boolean {
  if (!settings?.enabled || !ISO.test(date) || (settings.closedDates ?? []).includes(date)) return false;
  return cleanPlan(settings[agent]).days.includes(weekday(date));
}

/** Slots on that date with space left. `exceptItemId`: the item being moved doesn't count against its own slot. */
export function freeSlots(settings: SlotSettings | undefined, agent: SlotAgent, date: string, booked: Booking[], exceptItemId?: string): SlotDef[] {
  if (!isSlotDay(settings, agent, date)) return [];
  const counts: Record<string, number> = {};
  booked.forEach((b) => { if (b.date === date && b.agent === agent && b.status === 'booked' && b.itemId !== exceptItemId) counts[b.start] = (counts[b.start] ?? 0) + 1; });
  return cleanPlan(settings![agent]).slots.filter((s) => (counts[s.start] ?? 0) < s.capacity);
}

/** First day from `fromOffset` days ahead (within two weeks) when either call type has slots. */
export function nextSlotDay(settings: SlotSettings | undefined, fromOffset = 1): string | undefined {
  for (let n = fromOffset; n < fromOffset + 14; n += 1) {
    const d = toIsoDate(addDays(startOfToday(), n));
    if (SLOT_AGENTS.some((a) => isSlotDay(settings, a, d))) return d;
  }
  return undefined;
}

const settingsRef = (fid: string) => doc(db!, 'facilities', fid, 'settings', 'slots');
const bookingRef = (fid: string, itemId: string) => doc(db!, 'facilities', fid, 'bookings', itemId);
const signedInUid = (actor: Actor) => scope().uid ?? actor.id;

export async function getSlotSettings(fid: string): Promise<SlotSettings> {
  if (isReal()) {
    const snap = await getDoc(settingsRef(fid));
    return withDefaults(snap.exists() ? (snap.data() as SlotSettings) : undefined);
  }
  return respond(withDefaults(JSON.parse(JSON.stringify(mockSettings[fid] ?? {}))));
}

export async function saveSlotSettings(fid: string, settings: SlotSettings, actor: Actor | undefined): Promise<SlotSettings> {
  if (!actor || !canEditSlots(actor.role)) throw new Error('Not allowed');
  // Only these fields may be stored (the database rules check the same); updatedBy is the signed-in user's id.
  const out: SlotSettings = {
    enabled: !!settings.enabled,
    baby: cleanPlan(settings.baby),
    pregnancy: cleanPlan(settings.pregnancy),
    closedDates: Array.from(new Set((settings.closedDates ?? []).filter((d) => ISO.test(d)))).sort(),
    updatedBy: signedInUid(actor),
    updatedByName: actor.name,
    updatedAt: new Date().toISOString(),
  };
  const detail = `${out.enabled ? 'on' : 'off'} · baby ${out.baby!.slots.length} · pregnancy ${out.pregnancy!.slots.length} · closed ${out.closedDates!.length}`;
  if (isReal()) {
    await setDoc(settingsRef(fid), out);
    addAudit(actor, 'slots.saved', fid, fid, detail);
    return out;
  }
  mockSettings[fid] = JSON.parse(JSON.stringify(out));
  addAudit(actor, 'slots.saved', fid, fid, detail);
  return respond(out);
}

/** Booked visits for one date, or (no date) every booked visit from a week ago onwards. Cancelled ones are left out. */
export async function getBookings(fid: string, date?: string): Promise<Booking[]> {
  if (isReal()) {
    const from = toIsoDate(addDays(startOfToday(), -7));
    const q = query(collection(db!, 'facilities', fid, 'bookings'), date ? where('date', '==', date) : where('date', '>=', from));
    const snap = await getDocs(q);
    return snap.docs.map((d) => ({ ...(d.data() as Booking), itemId: d.id })).filter((b) => b.status === 'booked');
  }
  return respond(mockBookings.filter((b) => b.facilityId === fid && b.status === 'booked' && (!date || b.date === date)).map((b) => ({ ...b })));
}

/** The live booking for one dose / check-up, if any. */
export async function getBooking(fid: string, itemId: string): Promise<Booking | undefined> {
  if (isReal()) {
    const snap = await getDoc(bookingRef(fid, itemId));
    const b = snap.exists() ? ({ ...(snap.data() as Booking), itemId: snap.id }) : undefined;
    return b?.status === 'booked' ? b : undefined;
  }
  const b = mockBookings.find((x) => x.facilityId === fid && x.itemId === itemId && x.status === 'booked');
  return respond(b && { ...b });
}

export type BookingInput = Pick<Booking, 'agent' | 'motherId' | 'itemId' | 'itemName' | 'date' | 'start' | 'end'>;

/** Reception books or moves a visit. Only a slot that exists that day and still has space is accepted. */
export async function setBooking(fid: string, input: BookingInput, actor: Actor | undefined): Promise<Booking> {
  if (!actor || !canEditSlots(actor.role)) throw new Error('Not allowed');
  const [settings, sameDay] = await Promise.all([getSlotSettings(fid), getBookings(fid, input.date)]);
  const slot = freeSlots(settings, input.agent, input.date, sameDay, input.itemId).find((s) => s.start === input.start && s.end === input.end);
  if (!slot) throw new Error('SLOT_FULL');
  // Only the allowed fields; hand bookings are always source 'reception' with the signed-in user's id.
  const b: Booking = {
    facilityId: fid,
    agent: input.agent,
    motherId: input.motherId,
    itemId: input.itemId,
    ...(input.itemName ? { itemName: input.itemName } : {}),
    date: input.date,
    start: slot.start,
    end: slot.end,
    status: 'booked',
    source: 'reception',
    bookedAt: new Date().toISOString(),
    bookedBy: signedInUid(actor),
    bookedByName: actor.name,
  };
  if (isReal()) {
    await setDoc(bookingRef(fid, input.itemId), b);
  } else {
    const i = mockBookings.findIndex((x) => x.facilityId === fid && x.itemId === input.itemId);
    if (i >= 0) mockBookings[i] = b; else mockBookings.push(b);
  }
  addAudit(actor, 'booking.set', input.itemId, fid, `${b.date} ${b.start}-${b.end}`);
  return isReal() ? b : respond(b);
}

/** Cancel = mark cancelled (never deleted), recording who cancelled and when. */
export async function cancelBooking(fid: string, itemId: string, actor: Actor | undefined): Promise<void> {
  if (!actor || !canEditSlots(actor.role)) throw new Error('Not allowed');
  const patch = { status: 'cancelled' as const, source: 'reception' as const, bookedAt: new Date().toISOString(), bookedBy: signedInUid(actor), bookedByName: actor.name };
  if (isReal()) {
    await updateDoc(bookingRef(fid, itemId), patch);
  } else {
    const b = mockBookings.find((x) => x.facilityId === fid && x.itemId === itemId);
    if (b) Object.assign(b, patch);
  }
  addAudit(actor, 'booking.cancelled', itemId, fid);
  if (!isReal()) return respond(undefined);
}

/** Today screen: each slot on that date with the mothers booked into it (first names only). */
export async function getVisitsByTime(fid: string, date: string): Promise<SlotVisits[]> {
  const [settings, booked, clinic] = await Promise.all([getSlotSettings(fid), getBookings(fid, date), loadClinic(fid)]);
  if (!settings.enabled) return [];
  const byId = new Map(clinic.mothers.map((m) => [m.id, m]));
  const rows: SlotVisits[] = [];
  SLOT_AGENTS.forEach((agent) => {
    const mine = booked.filter((b) => b.agent === agent && byId.has(b.motherId));
    const slots = isSlotDay(settings, agent, date) ? cleanPlan(settings[agent]).slots : [];
    // A booking whose slot was later removed still shows, under its own time (capacity 0 = no longer a slot).
    const extra = mine.filter((b) => !slots.some((s) => s.start === b.start)).map((b) => ({ start: b.start, end: b.end, capacity: 0 }));
    const all = [...slots, ...extra.filter((s, i) => extra.findIndex((x) => x.start === s.start) === i)].sort((a, b) => a.start.localeCompare(b.start));
    all.forEach((slot) => rows.push({
      agent,
      slot,
      booked: mine.filter((b) => b.start === slot.start).map((b) => ({ motherId: b.motherId, firstName: byId.get(b.motherId)!.name.split(' ')[0], itemName: b.itemName, source: b.source })),
    }));
  });
  return rows;
}
